import { applyMiddleware } from "redux";
import { createDynamicMiddlewares } from "redux-dynamic-middlewares";
import createSagaMiddleware, { SagaMiddleware } from "redux-saga";

import { composeWithDevToolsLogOnlyInProduction } from "@redux-devtools/extension";

import { createInjectableStore } from "../redux/createInjectableStore";
import { RootReducerMap } from "../redux/reducers/rootReducer";

const dynamicMiddlewares = createDynamicMiddlewares();

let isSagaMiddlewareConfigured = false;

export const initializeClientReduxStore = () => {
  // Grab the state from a global variable injected into the server-generated HTML
  const preloadedState = window.__PRELOADED_STATE__;

  // Allow the passed state to be garbage-collected
  delete window.__PRELOADED_STATE__;

  // Create Redux store with initial state
  return {
    store: createInjectableStore(
      RootReducerMap,
      preloadedState,
      composeWithDevToolsLogOnlyInProduction(applyMiddleware(dynamicMiddlewares.enhancer))
    ),
    preloadedState,
  };
};

export const configureSagaMiddleware = <ContextType extends object = Record<string, never>>(
  context?: ContextType
): SagaMiddleware => {
  if (isSagaMiddlewareConfigured) {
    console.warn("Double configuration of saga middleware detected. Ignoring.");
  }

  const sagaMiddleware = createSagaMiddleware({
    context,
    onError: _error => {},
  });
  dynamicMiddlewares.addMiddleware(sagaMiddleware);
  isSagaMiddlewareConfigured = true;
  return sagaMiddleware;
};

type DynamicMiddleware = Parameters<typeof dynamicMiddlewares.addMiddleware>[0];

// Keeps track of the middlewares that have been added
const addedMiddlewares = new WeakSet<DynamicMiddleware>();

/**
 * Adds a dynamic middleware to the store. Only one instance of a middleware can be added.
 * This is useful to make sure that the same middleware is not added twice.
 * @param middleware The middleware to add to the store
 */
export function addDynamicMiddleware(middleware: DynamicMiddleware) {
  if (addedMiddlewares.has(middleware)) {
    return;
  }

  dynamicMiddlewares.addMiddleware(middleware);
  addedMiddlewares.add(middleware);
}

/**
 * Removes a dynamic middleware from the store. If the middleware is not added, this is a no-op.
 * @param middleware The middleware to remove from the store
 */
export function removeDynamicMiddleware(middleware: DynamicMiddleware) {
  addedMiddlewares.delete(middleware);
  dynamicMiddlewares.removeMiddleware(middleware);
}
